// ============================================================
// Question Bank Page — Visual MCQ, Subjective & Speaking Builders
// ============================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
    Plus, Edit2, Trash2, Download, Upload, BookOpen, Search,
    ChevronDown, ChevronUp, X, GripVertical, Check, AlertTriangle,
    Copy, Filter, Tag, Eye, Folder, FolderOpen, FolderPlus, Sparkles,
    Video, HelpCircle, Save, ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Types ────────────────────────────────────────────────────

interface McqOption {
    id: string;
    text: string;
    isCorrect: boolean;
}

interface RubricCriterion {
    id: string;
    name: string;
    weight: number;
    description: string;
    maxScore: number;
}

interface QuestionConfig {
    options?: McqOption[];
    correctOptionIds?: string[];
    rubric?: RubricCriterion[];
    sampleAnswer?: string;
    scoringGuidelines?: string;
    maxScore?: number;
}

// ── Main Component ───────────────────────────────────────────

export function QuestionBankPage() {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('');
    const [previewQuestion, setPreviewQuestion] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Category / Folder States
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>(''); // '' = All, 'unassigned' = General/Unassigned
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderJobId, setNewFolderJobId] = useState('');
    const [renamingFolder, setRenamingFolder] = useState<any>(null);
    const [renamingFolderName, setRenamingFolderName] = useState('');

    // AI Generator Modal State
    const [showAiModal, setShowAiModal] = useState(false);

    // Queries
    const { data: categoriesData } = useQuery({
        queryKey: ['categories'],
        queryFn: () => api.get('/questions/categories'),
    });
    const categories = categoriesData?.data || [];

    const { data: jobsData } = useQuery({
        queryKey: ['jobs'],
        queryFn: () => api.get('/jobs'),
    });
    const jobs = jobsData?.data || [];

    const { data, isLoading } = useQuery({
        queryKey: ['questions', searchQuery, filterType, filterDifficulty, selectedCategoryId, page],
        queryFn: () => {
            const params = new URLSearchParams();
            if (searchQuery) params.set('search', searchQuery);
            if (filterType) params.set('type', filterType);
            if (filterDifficulty) params.set('difficulty', filterDifficulty);
            if (selectedCategoryId) params.set('categoryId', selectedCategoryId);
            params.set('page', String(page));
            params.set('limit', '50');
            return api.get(`/questions?${params.toString()}`);
        },
    });

    // Mutations
    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/questions/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['questions'] });
            toast.success('Question archived');
        },
    });

    const createFolderMutation = useMutation({
        mutationFn: (newFolder: any) => api.post('/questions/categories', newFolder),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            setShowCreateFolder(false);
            setNewFolderName('');
            setNewFolderJobId('');
            toast.success('Folder created successfully');
        },
        onError: (err: any) => toast.error(err.message || 'Failed to create folder'),
    });

    const updateFolderMutation = useMutation({
        mutationFn: ({ id, body }: { id: string; body: any }) => api.put(`/questions/categories/${id}`, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            setRenamingFolder(null);
            setRenamingFolderName('');
            toast.success('Folder updated');
        },
        onError: (err: any) => toast.error(err.message || 'Failed to update folder'),
    });

    const deleteFolderMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/questions/categories/${id}`),
        onSuccess: (_data, id) => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            queryClient.invalidateQueries({ queryKey: ['questions'] });
            if (selectedCategoryId === id) setSelectedCategoryId('');
            toast.success('Folder deleted');
        },
        onError: (err: any) => toast.error(err.message || 'Failed to delete folder'),
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            for (const id of ids) {
                await api.delete(`/questions/${id}`);
            }
            return ids.length;
        },
        onSuccess: (count) => {
            setSelectedIds(new Set());
            queryClient.invalidateQueries({ queryKey: ['questions'] });
            toast.success(`${count} questions archived`);
        },
    });

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === questions.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(questions.map((q: any) => q.id)));
        }
    };

    const handleBulkDelete = () => {
        if (!confirm(`Archive ${selectedIds.size} selected question(s)?`)) return;
        bulkDeleteMutation.mutate([...selectedIds]);
    };

    const questions = data?.data || [];
    const pagination = data?.pagination;

    const handleExport = async () => {
        try {
            const result = await api.get('/questions/export/all');
            const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'question-bank-export.json';
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Questions exported!');
        } catch (err: any) {
            toast.error(err.message || 'Export failed');
        }
    };

    const handleImport = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e: any) => {
            const file = e.target.files[0];
            if (!file) return;
            try {
                const text = await file.text();
                const parsed = JSON.parse(text);
                const questionsList = Array.isArray(parsed) ? parsed : (parsed.questions || []);
                if (!questionsList.length) return toast.error('No questions found in file');
                const result = await api.post('/questions/import', { questions: questionsList });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                toast.success(`Imported ${result.data.imported} questions!`);
            } catch (err: any) {
                toast.error(err.message || 'Import failed — check file format');
            }
        };
        input.click();
    };

    const handleDownloadTemplate = () => {
        const a = document.createElement('a');
        a.href = '/question-bank-template.json';
        a.download = 'question-bank-template.json';
        a.click();
        toast.success('Template downloaded!');
    };

    const openEdit = (q: any) => {
        setEditingQuestion(q);
        setShowCreate(true);
    };

    const handleCreateFolder = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newFolderName.trim()) return;
        createFolderMutation.mutate({
            name: newFolderName,
            jobId: newFolderJobId || null
        });
    };

    const handleRenameFolder = (e: React.FormEvent) => {
        e.preventDefault();
        if (!renamingFolderName.trim() || !renamingFolder) return;
        updateFolderMutation.mutate({
            id: renamingFolder.id,
            body: { name: renamingFolderName }
        });
    };

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Question Bank</h1>
                    <p className="subtitle">Manage folder categories, create questions, or auto-generate with AI</p>
                </div>
                <div className="flex gap-3">
                    <button className="btn btn-ghost" onClick={handleDownloadTemplate} title="Download JSON template">
                        <BookOpen size={16} /> Template
                    </button>
                    <button className="btn btn-ghost" onClick={handleImport}>
                        <Upload size={16} /> Import
                    </button>
                    <button className="btn btn-secondary" onClick={handleExport}>
                        <Download size={16} /> Export
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowAiModal(true)} style={{ gap: '6px', borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}>
                        <Sparkles size={16} /> AI Generator
                    </button>
                    <button className="btn btn-primary" onClick={() => { setEditingQuestion(null); setShowCreate(true); }}>
                        <Plus size={16} /> New Question
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="card mb-6" style={{ padding: '16px 20px' }}>
                <div className="flex gap-4 items-center" style={{ flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                            <input
                                className="form-input"
                                placeholder="Search questions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ paddingLeft: 36 }}
                            />
                        </div>
                    </div>
                    <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}
                        style={{ width: 160 }}>
                        <option value="">All Types</option>
                        <option value="mcq">MCQ</option>
                        <option value="subjective">Subjective</option>
                        <option value="speaking">Speaking</option>
                    </select>
                    <select className="form-select" value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}
                        style={{ width: 160 }}>
                        <option value="">All Difficulty</option>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                    </select>
                    {(filterType || filterDifficulty || searchQuery || selectedCategoryId) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => { setFilterType(''); setFilterDifficulty(''); setSearchQuery(''); setSelectedCategoryId(''); }}>
                            <X size={14} /> Clear All
                        </button>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* Left Pane: Category / Folder tree sidebar */}
                <aside style={{ width: 260, flexShrink: 0 }} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-secondary)' }}>Folders</h3>
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateFolder(true)} style={{ padding: 4 }} title="New folder">
                            <FolderPlus size={16} />
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                            onClick={() => setSelectedCategoryId('')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', borderRadius: 8,
                                background: selectedCategoryId === '' ? 'var(--color-accent-muted)' : 'transparent',
                                color: selectedCategoryId === '' ? 'var(--color-accent)' : 'var(--color-text-primary)',
                                textAlign: 'left', cursor: 'pointer', fontWeight: selectedCategoryId === '' ? 600 : 500, fontSize: '13px'
                            }}
                        >
                            <FolderOpen size={16} /> All Questions
                        </button>

                        <button
                            onClick={() => setSelectedCategoryId('unassigned')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', borderRadius: 8,
                                background: selectedCategoryId === 'unassigned' ? 'var(--color-accent-muted)' : 'transparent',
                                color: selectedCategoryId === 'unassigned' ? 'var(--color-accent)' : 'var(--color-text-primary)',
                                textAlign: 'left', cursor: 'pointer', fontWeight: selectedCategoryId === 'unassigned' ? 600 : 500, fontSize: '13px'
                            }}
                        >
                            <Folder size={16} /> General Questions
                        </button>

                        {/* User custom & job categories */}
                        {categories.map((cat: any) => {
                            const isSelected = selectedCategoryId === cat.id;
                            const job = jobs.find((j: any) => j.id === cat.jobId);

                            return (
                                <div
                                    key={cat.id}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 8,
                                        background: isSelected ? 'var(--color-accent-muted)' : 'transparent',
                                        paddingRight: 6
                                    }}
                                >
                                    <button
                                        onClick={() => setSelectedCategoryId(cat.id)}
                                        style={{
                                            flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none',
                                            background: 'transparent',
                                            color: isSelected ? 'var(--color-accent)' : 'var(--color-text-primary)',
                                            textAlign: 'left', cursor: 'pointer', fontWeight: isSelected ? 600 : 500, fontSize: '13px',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}
                                        title={cat.name}
                                    >
                                        <Folder size={16} />
                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {cat.name}
                                            {job && <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 400 }}>Job: {job.title}</span>}
                                        </div>
                                    </button>

                                    <div className="flex gap-1" style={{ opacity: isSelected ? 1 : 0.4 }}>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => {
                                                setRenamingFolder(cat);
                                                setRenamingFolderName(cat.name);
                                            }}
                                            style={{ padding: 2, background: 'none' }}
                                        >
                                            <Edit2 size={12} />
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => {
                                                if (confirm(`Delete folder "${cat.name}"? Questions inside will be moved to General.`)) {
                                                    deleteFolderMutation.mutate(cat.id);
                                                }
                                            }}
                                            style={{ padding: 2, color: 'var(--color-error)', background: 'none' }}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* Right Pane: Bulk Actions Bar + Questions List Grid */}
                <div style={{ flex: 1 }}>
                    {/* Bulk Actions Bar */}
                    {selectedIds.size > 0 && (
                        <div className="card mb-4" style={{ padding: '12px 20px', background: 'rgba(99,102,241,0.08)', border: '1px solid var(--color-border)' }}>
                            <div className="flex items-center gap-4" style={{ justifyContent: 'space-between' }}>
                                <div className="flex items-center gap-3">
                                    <input type="checkbox" checked={selectedIds.size === questions.length}
                                        onChange={toggleSelectAll}
                                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-accent)' }} />
                                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                        {selectedIds.size} question{selectedIds.size > 1 ? 's' : ''} selected
                                    </span>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setSelectedIds(new Set())}
                                        style={{ fontSize: '12px' }}>
                                        Clear selection
                                    </button>
                                </div>
                                <button className="btn btn-sm" onClick={handleBulkDelete}
                                    disabled={bulkDeleteMutation.isPending}
                                    style={{ background: 'var(--color-error)', color: 'white', fontWeight: 600 }}>
                                    <Trash2 size={14} /> {bulkDeleteMutation.isPending ? 'Deleting...' : `Archive ${selectedIds.size}`}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Questions List */}
                    {questions.length === 0 && !isLoading ? (
                        <div className="card">
                            <div className="empty-state">
                                <div className="icon">📝</div>
                                <h3>No questions in this folder</h3>
                                <p>Build questions in this folder category to start designing assessments.</p>
                                <button className="btn btn-primary mt-4" onClick={() => { setEditingQuestion(null); setShowCreate(true); }}>
                                    <Plus size={16} /> Create Question
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Select All Row */}
                            <div className="flex items-center gap-3 mb-3" style={{ paddingLeft: 4 }}>
                                <input type="checkbox"
                                    checked={questions.length > 0 && selectedIds.size === questions.length}
                                    onChange={toggleSelectAll}
                                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-accent)' }} />
                                <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                                    {selectedIds.size === questions.length && questions.length > 0 ? 'Deselect all' : 'Select all'}
                                    {pagination && ` (${pagination.total} total)`}
                                </span>
                            </div>
                            <div className="card-grid card-grid-2">
                                {questions.map((q: any) => (
                                    <QuestionCard
                                        key={q.id}
                                        question={q}
                                        categories={categories}
                                        selected={selectedIds.has(q.id)}
                                        onToggleSelect={() => toggleSelect(q.id)}
                                        onEdit={() => openEdit(q)}
                                        onDelete={() => { if (confirm('Archive this question?')) deleteMutation.mutate(q.id); }}
                                        onPreview={() => setPreviewQuestion(q)}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {pagination && pagination.totalPages > 1 && (
                                <div className="flex items-center gap-3" style={{ justifyContent: 'center', marginTop: 24 }}>
                                    <button className="btn btn-ghost btn-sm" disabled={page <= 1}
                                        onClick={() => setPage(p => Math.max(1, p - 1))}>
                                        ← Previous
                                    </button>
                                    <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} questions)
                                    </span>
                                    <button className="btn btn-ghost btn-sm" disabled={page >= pagination.totalPages}
                                        onClick={() => setPage(p => p + 1)}>
                                        Next →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Folder Create Modal */}
            {showCreateFolder && (
                <div className="modal-overlay" onClick={() => setShowCreateFolder(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <div className="flex justify-between items-center mb-6">
                            <h2>Create New Folder</h2>
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateFolder(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleCreateFolder}>
                            <div className="form-group">
                                <label className="form-label">Folder Name *</label>
                                <input className="form-input" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="e.g. Frontend Engineering" required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Link to Job Profile (Optional)</label>
                                <select className="form-select" value={newFolderJobId} onChange={e => setNewFolderJobId(e.target.value)}>
                                    <option value="">-- No Link --</option>
                                    {jobs.map((j: any) => (
                                        <option key={j.id} value={j.id}>{j.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setShowCreateFolder(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1" disabled={createFolderMutation.isPending}>
                                    {createFolderMutation.isPending ? 'Creating...' : 'Create Folder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Folder Rename Modal */}
            {renamingFolder && (
                <div className="modal-overlay" onClick={() => setRenamingFolder(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <div className="flex justify-between items-center mb-6">
                            <h2>Rename Folder</h2>
                            <button className="btn btn-ghost btn-sm" onClick={() => setRenamingFolder(null)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleRenameFolder}>
                            <div className="form-group">
                                <label className="form-label">Folder Name *</label>
                                <input className="form-input" value={renamingFolderName} onChange={e => setRenamingFolderName(e.target.value)} required />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setRenamingFolder(null)}>Cancel</button>
                                <button type="submit" className="btn btn-primary flex-1" disabled={updateFolderMutation.isPending}>
                                    {updateFolderMutation.isPending ? 'Saving...' : 'Rename'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create / Edit Modal */}
            {showCreate && (
                <QuestionEditorModal
                    question={editingQuestion}
                    categories={categories}
                    onClose={() => { setShowCreate(false); setEditingQuestion(null); }}
                    onSaved={() => {
                        queryClient.invalidateQueries({ queryKey: ['questions'] });
                        setShowCreate(false);
                        setEditingQuestion(null);
                    }}
                />
            )}

            {/* AI Generator Modal */}
            {showAiModal && (
                <AiGeneratorModal
                    categories={categories}
                    onClose={() => setShowAiModal(false)}
                    onSaved={() => {
                        queryClient.invalidateQueries({ queryKey: ['questions'] });
                        setShowAiModal(false);
                    }}
                />
            )}

            {/* Preview Modal */}
            {previewQuestion && (
                <QuestionPreviewModal
                    question={previewQuestion}
                    onClose={() => setPreviewQuestion(null)}
                />
            )}
        </div>
    );
}

// ── Question Card ────────────────────────────────────────────

function QuestionCard({ question: q, categories, selected, onToggleSelect, onEdit, onDelete, onPreview }: {
    question: any; categories: any[]; selected: boolean; onToggleSelect: () => void; onEdit: () => void; onDelete: () => void; onPreview: () => void;
}) {
    const config = q.currentVersion?.config || q.config || {};
    const optionCount = config.options?.length || 0;
    const rubricCount = config.rubric?.length || 0;
    const folder = categories.find((c: any) => c.id === q.categoryId);

    return (
        <div className="card" style={{ position: 'relative', outline: selected ? '2px solid var(--color-accent)' : 'none', display: 'flex', flexDirection: 'column' }}>
            {/* Selection Checkbox */}
            <input type="checkbox" checked={selected} onChange={onToggleSelect}
                style={{ position: 'absolute', top: 12, right: 12, width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-accent)' }} />
            
            <div className="flex justify-between items-center mb-3">
                <div className="flex gap-2 items-center">
                    <span className={`badge ${q.type === 'mcq' ? 'badge-accent' : q.type === 'speaking' ? 'badge-warning' : 'badge-info'}`}>
                        {q.type === 'mcq' ? '🔤 MCQ' : q.type === 'speaking' ? '🗣️ Speaking' : '✍️ Subjective'}
                    </span>
                    <span className={`badge ${q.difficulty === 'easy' ? 'badge-success' : q.difficulty === 'hard' ? 'badge-error' : 'badge-warning'}`}>
                        {q.difficulty}
                    </span>
                </div>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                    v{q.currentVersion?.versionNumber || 1}
                </span>
            </div>

            <p style={{ fontWeight: 600, fontSize: '14px', marginBottom: 12, lineHeight: 1.5, minHeight: 42, color: 'var(--color-text-primary)' }}>
                {(q.currentVersion?.prompt || 'No prompt').substring(0, 120)}
                {(q.currentVersion?.prompt || '').length > 120 ? '…' : ''}
            </p>

            {/* Folder Name & Info */}
            <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: 12, marginTop: 'auto' }}>
                <div style={{ marginBottom: 4 }}>
                    {q.type === 'mcq' && optionCount > 0 && (
                        <span>{optionCount} options · {config.options?.filter((o: any) => o.isCorrect).length || config.correctOptionIds?.length || 0} correct</span>
                    )}
                    {q.type === 'subjective' && rubricCount > 0 && (
                        <span>{rubricCount} rubric criteria</span>
                    )}
                    {q.type === 'speaking' && rubricCount > 0 && (
                        <span>{rubricCount} evaluation parameters</span>
                    )}
                </div>
                {folder && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-accent)', fontWeight: 500 }}>
                        <Folder size={12} /> Folder: {folder.name}
                    </div>
                )}
            </div>

            {/* Tags */}
            {q.tags && q.tags.length > 0 && (
                <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                    {q.tags.slice(0, 4).map((tag: string) => (
                        <span key={tag} className="badge badge-accent" style={{ fontSize: '10px' }}>{tag}</span>
                    ))}
                </div>
            )}

            <div className="flex gap-2" style={{ marginTop: 'auto' }}>
                <button className="btn btn-ghost btn-sm" onClick={onPreview}><Eye size={14} /> Preview</button>
                <button className="btn btn-secondary btn-sm flex-1" onClick={onEdit}><Edit2 size={14} /> Edit</button>
                <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: 'var(--color-error)' }}><Trash2 size={14} /></button>
            </div>
        </div>
    );
}

// ── Question Editor Modal ────────────────────────────────────

function QuestionEditorModal({ question, categories, onClose, onSaved }: {
    question: any | null; categories: any[]; onClose: () => void; onSaved: () => void;
}) {
    const isEditing = !!question;
    const existingConfig = question?.currentVersion?.config || question?.config || {};
    const existingPrompt = question?.currentVersion?.prompt || '';

    const [type, setType] = useState(question?.type || 'mcq');
    const [prompt, setPrompt] = useState(existingPrompt);
    const [difficulty, setDifficulty] = useState(question?.difficulty || 'medium');
    const [categoryId, setCategoryId] = useState(question?.categoryId || '');
    const [tags, setTags] = useState<string[]>(question?.tags || []);
    const [tagInput, setTagInput] = useState('');

    // MCQ state
    const [options, setOptions] = useState<McqOption[]>(
        existingConfig.options || [
            { id: '1', text: '', isCorrect: false },
            { id: '2', text: '', isCorrect: false },
        ]
    );

    // Subjective state
    const [rubric, setRubric] = useState<RubricCriterion[]>(
        existingConfig.rubric || []
    );
    const [sampleAnswer, setSampleAnswer] = useState(existingConfig.sampleAnswer || '');
    const [scoringGuidelines, setScoringGuidelines] = useState(existingConfig.scoringGuidelines || '');
    const [maxScore, setMaxScore] = useState(existingConfig.maxScore || 10);

    const [saving, setSaving] = useState(false);

    const addOption = () => {
        setOptions([...options, { id: String(Date.now()), text: '', isCorrect: false }]);
    };

    const removeOption = (id: string) => {
        if (options.length <= 2) return toast.error('At least 2 options required');
        setOptions(options.filter(o => o.id !== id));
    };

    const updateOption = (id: string, updates: Partial<McqOption>) => {
        setOptions(options.map(o => o.id === id ? { ...o, ...updates } : o));
    };

    const addRubricCriterion = () => {
        setRubric([...rubric, {
            id: String(Date.now()),
            name: '',
            weight: 1,
            description: '',
            maxScore: 10,
        }]);
    };

    const removeRubricCriterion = (id: string) => {
        setRubric(rubric.filter(r => r.id !== id));
    };

    const updateRubricCriterion = (id: string, updates: Partial<RubricCriterion>) => {
        setRubric(rubric.map(r => r.id === id ? { ...r, ...updates } : r));
    };

    const addTag = () => {
        const trimmed = tagInput.trim().toLowerCase();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
        }
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        setTags(tags.filter(t => t !== tag));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!prompt.trim()) return toast.error('Question prompt is required');

        if (type === 'mcq') {
            if (options.some(o => !o.text.trim())) return toast.error('All options must have text');
            if (!options.some(o => o.isCorrect)) return toast.error('Mark at least one correct answer');
        }

        const config: QuestionConfig = {};
        if (type === 'mcq') {
            config.options = options;
            config.correctOptionIds = options.filter(o => o.isCorrect).map(o => o.id);
        } else {
            config.rubric = rubric;
            config.sampleAnswer = sampleAnswer;
            config.scoringGuidelines = scoringGuidelines;
            config.maxScore = maxScore;
        }

        setSaving(true);
        try {
            const body = {
                type,
                prompt,
                config,
                tags,
                difficulty,
                categoryId: categoryId || null
            };

            if (isEditing) {
                await api.put(`/questions/${question.id}`, body);
                toast.success('Question updated (new version created)');
            } else {
                await api.post('/questions', body);
                toast.success('Question created!');
            }
            onSaved();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-6">
                    <h2>{isEditing ? 'Edit Question' : 'Create Question'}</h2>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit}>
                    {!isEditing && (
                        <div className="form-group">
                            <label className="form-label">Question Type</label>
                            <div className="flex gap-3">
                                <button type="button" className={`btn ${type === 'mcq' ? 'btn-primary' : 'btn-secondary'} flex-1`}
                                    onClick={() => setType('mcq')}>
                                    🔤 MCQ
                                </button>
                                <button type="button" className={`btn ${type === 'subjective' ? 'btn-primary' : 'btn-secondary'} flex-1`}
                                    onClick={() => setType('subjective')}>
                                    ✍️ Subjective
                                </button>
                                <button type="button" className={`btn ${type === 'speaking' ? 'btn-primary' : 'btn-secondary'} flex-1`}
                                    onClick={() => setType('speaking')}>
                                    🗣️ Speaking Video
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Folder / Category</label>
                        <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                            <option value="">-- General / Unassigned --</option>
                            {categories.map((c: any) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Question Prompt *</label>
                        <textarea className="form-textarea" value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Write your question here..."
                            required rows={3} style={{ fontSize: '14px' }} />
                    </div>

                    <div className="flex gap-4">
                        <div className="form-group" style={{ flex: 1 }}>
                            <label className="form-label">Difficulty</label>
                            <div className="flex gap-2">
                                {['easy', 'medium', 'hard'].map(d => (
                                    <button key={d} type="button"
                                        className={`btn btn-sm flex-1 ${difficulty === d ? 'btn-primary' : 'btn-secondary'}`}
                                        style={difficulty === d ? {
                                            background: d === 'easy' ? '#10b981' : d === 'hard' ? '#ef4444' : '#f59e0b',
                                            borderColor: 'transparent',
                                            color: 'white'
                                        } : {}}
                                        onClick={() => setDifficulty(d)}>
                                        {d.charAt(0).toUpperCase() + d.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Tags</label>
                        <div className="flex gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
                            {tags.map(tag => (
                                <span key={tag} className="badge badge-accent" style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                                    onClick={() => removeTag(tag)}>
                                    {tag} <X size={12} />
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input className="form-input" value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                                placeholder="Type a tag and press Enter" style={{ flex: 1 }} />
                            <button type="button" className="btn btn-ghost btn-sm" onClick={addTag}>
                                <Tag size={14} /> Add
                            </button>
                        </div>
                    </div>

                    {type === 'mcq' && (
                        <div className="form-group">
                            <label className="form-label">Answer Options</label>
                            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: 12 }}>
                                Click the circle to mark the correct answer(s)
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {options.map((opt, i) => (
                                    <div key={opt.id} className="flex items-center gap-3" style={{
                                        padding: '10px 14px',
                                        background: opt.isCorrect ? 'rgba(16, 185, 129, 0.08)' : 'var(--color-bg-tertiary)',
                                        borderRadius: 10,
                                        border: opt.isCorrect ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid transparent',
                                        transition: 'all 0.15s ease'
                                    }}>
                                        <button type="button"
                                            onClick={() => updateOption(opt.id, { isCorrect: !opt.isCorrect })}
                                            style={{
                                                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                                                background: opt.isCorrect ? '#10b981' : 'transparent',
                                                border: opt.isCorrect ? '2px solid #10b981' : '2px solid #6b7280',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', transition: 'all 0.15s ease'
                                            }}>
                                            {opt.isCorrect && <Check size={14} color="white" />}
                                        </button>
                                        <span style={{ color: 'var(--color-text-muted)', fontSize: '13px', fontWeight: 600, width: 20 }}>
                                            {String.fromCharCode(65 + i)}
                                        </span>
                                        <input
                                            value={opt.text}
                                            onChange={(e) => updateOption(opt.id, { text: e.target.value })}
                                            placeholder={`Option ${String.fromCharCode(65 + i)}`}
                                            style={{
                                                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                                fontSize: '14px', color: 'var(--color-text-primary)', padding: 0,
                                            }}
                                        />
                                        {options.length > 2 && (
                                            <button type="button" className="btn btn-ghost btn-sm"
                                                onClick={() => removeOption(opt.id)} style={{ padding: 4, color: 'var(--color-error)' }}>
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={addOption}>
                                <Plus size={14} /> Add Option
                            </button>
                        </div>
                    )}

                    {(type === 'subjective' || type === 'speaking') && (
                        <>
                            <div className="form-group">
                                <label className="form-label">Maximum Score</label>
                                <input type="number" className="form-input" value={maxScore}
                                    onChange={(e) => setMaxScore(Number(e.target.value))}
                                    min={1} max={100} style={{ width: 120 }} />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Scoring Rubric</label>
                                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: 12 }}>
                                    Define criteria the AI will use to evaluate candidate responses
                                </p>

                                {rubric.length === 0 ? (
                                    <div style={{
                                        padding: 24, textAlign: 'center', background: 'var(--color-bg-tertiary)',
                                        borderRadius: 10, color: 'var(--color-text-muted)', fontSize: '13px'
                                    }}>
                                        No rubric criteria defined. Click "Add Criterion" to start.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {rubric.map((criterion) => (
                                            <div key={criterion.id} style={{
                                                padding: 16, background: 'var(--color-bg-tertiary)',
                                                borderRadius: 10, border: '1px solid var(--color-border)',
                                            }}>
                                                <div className="flex gap-3 items-center mb-3">
                                                    <input value={criterion.name}
                                                        onChange={(e) => updateRubricCriterion(criterion.id, { name: e.target.value })}
                                                        placeholder="Criterion name (e.g. Code Quality)"
                                                        style={{
                                                            flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                                            fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', padding: 0,
                                                        }} />
                                                    <div className="flex items-center gap-2" style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                                        <span>Weight:</span>
                                                        <input type="number" value={criterion.weight}
                                                            onChange={(e) => updateRubricCriterion(criterion.id, { weight: Number(e.target.value) })}
                                                            min={1} max={100}
                                                            style={{
                                                                width: 50, background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
                                                                borderRadius: 6, padding: '4px 8px', fontSize: '13px', color: 'var(--color-text-primary)',
                                                                textAlign: 'center'
                                                            }} />
                                                        <span>%</span>
                                                    </div>
                                                    <div className="flex items-center gap-2" style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                                        <span>Max:</span>
                                                        <input type="number" value={criterion.maxScore}
                                                            onChange={(e) => updateRubricCriterion(criterion.id, { maxScore: Number(e.target.value) })}
                                                            min={1} max={100}
                                                            style={{
                                                                width: 50, background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
                                                                borderRadius: 6, padding: '4px 8px', fontSize: '13px', color: 'var(--color-text-primary)',
                                                                textAlign: 'center'
                                                            }} />
                                                    </div>
                                                    <button type="button" onClick={() => removeRubricCriterion(criterion.id)}
                                                        style={{ color: 'var(--color-error)', cursor: 'pointer', background: 'none', border: 'none', padding: 4 }}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <textarea
                                                    value={criterion.description}
                                                    onChange={(e) => updateRubricCriterion(criterion.id, { description: e.target.value })}
                                                    placeholder="Describe what the AI should look for in the response..."
                                                    rows={2}
                                                    style={{
                                                        width: '100%', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
                                                        borderRadius: 8, padding: '8px 12px', fontSize: '13px', color: 'var(--color-text-secondary)',
                                                        resize: 'vertical', outline: 'none',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={addRubricCriterion}>
                                    <Plus size={14} /> Add Criterion
                                </button>
                            </div>

                            {type === 'subjective' && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Sample Answer (Optional)</label>
                                        <textarea className="form-textarea" value={sampleAnswer}
                                            onChange={(e) => setSampleAnswer(e.target.value)}
                                            placeholder="Provide an ideal answer for reference..."
                                            rows={3} />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Scoring Guidelines (Optional)</label>
                                        <textarea className="form-textarea" value={scoringGuidelines}
                                            onChange={(e) => setScoringGuidelines(e.target.value)}
                                            placeholder="Additional instructions for the AI scorer..."
                                            rows={2} />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Submit */}
                    <div className="flex gap-3 mt-6">
                        <button type="button" className="btn btn-secondary flex-1" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary flex-1" disabled={saving}>
                            {saving ? 'Saving...' : isEditing ? 'Update Question' : 'Create Question'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Question Preview Modal ───────────────────────────────────

function QuestionPreviewModal({ question: q, onClose }: { question: any; onClose: () => void }) {
    const config = q.currentVersion?.config || q.config || {};
    const prompt = q.currentVersion?.prompt || '';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-2">
                        <span className={`badge ${q.type === 'mcq' ? 'badge-accent' : q.type === 'speaking' ? 'badge-warning' : 'badge-info'}`}>
                            {q.type === 'mcq' ? 'MCQ' : q.type === 'speaking' ? 'Speaking' : 'Subjective'}
                        </span>
                        <span className={`badge ${q.difficulty === 'easy' ? 'badge-success' : q.difficulty === 'hard' ? 'badge-error' : 'badge-warning'}`}>
                            {q.difficulty}
                        </span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                </div>

                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: 16, lineHeight: 1.5 }}>{prompt}</h3>

                {q.type === 'mcq' && config.options && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                        {config.options.map((opt: any, i: number) => (
                            <div key={opt.id} style={{
                                padding: '10px 14px', borderRadius: 8,
                                background: opt.isCorrect || config.correctOptionIds?.includes(opt.id)
                                    ? 'var(--color-success-bg)' : 'var(--color-bg-tertiary)',
                                border: opt.isCorrect || config.correctOptionIds?.includes(opt.id)
                                    ? '1px solid var(--color-success)' : '1px solid transparent',
                                display: 'flex', alignItems: 'center', gap: 10,
                            }}>
                                <span style={{ fontWeight: 600, color: 'var(--color-text-muted)', fontSize: '13px' }}>
                                    {String.fromCharCode(65 + i)}
                                </span>
                                <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{opt.text}</span>
                                {(opt.isCorrect || config.correctOptionIds?.includes(opt.id)) && (
                                    <Check size={16} color="var(--color-success)" style={{ marginLeft: 'auto' }} />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {(q.type === 'subjective' || q.type === 'speaking') && config.rubric && config.rubric.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontWeight: 600, fontSize: '14px', marginBottom: 8, color: 'var(--color-text-secondary)' }}>Evaluation Rubric</h4>
                        {config.rubric.map((c: any) => (
                            <div key={c.id} style={{
                                padding: '10px 14px', background: 'var(--color-bg-tertiary)', borderRadius: 8, marginBottom: 8,
                                border: '1px solid var(--color-border)'
                            }}>
                                <div className="flex justify-between">
                                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>{c.name}</span>
                                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Weight: {c.weight}% · Max: {c.maxScore}</span>
                                </div>
                                {c.description && <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 4 }}>{c.description}</p>}
                            </div>
                        ))}
                    </div>
                )}

                {q.type === 'subjective' && config.sampleAnswer && (
                    <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontWeight: 600, fontSize: '14px', marginBottom: 8, color: 'var(--color-text-secondary)' }}>Sample Answer</h4>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', background: 'var(--color-bg-tertiary)', padding: 12, borderRadius: 8, border: '1px solid var(--color-border)' }}>
                            {config.sampleAnswer}
                        </p>
                    </div>
                )}

                {q.tags && q.tags.length > 0 && (
                    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                        {q.tags.map((t: string) => <span key={t} className="badge badge-accent">{t}</span>)}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── AI Generator Modal ───────────────────────────────────────

function AiGeneratorModal({ categories, onClose, onSaved }: {
    categories: any[]; onClose: () => void; onSaved: () => void;
}) {
    const queryClient = useQueryClient();
    const [step, setStep] = useState<'input' | 'preview'>('input');
    
    // Step 1 parameters
    const [jobTitle, setJobTitle] = useState('');
    const [keySkills, setKeySkills] = useState('');
    const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
    const [type, setType] = useState<'mcq' | 'subjective' | 'speaking'>('mcq');
    const [count, setCount] = useState(3);
    const [generating, setGenerating] = useState(false);
    
    // Step 2 review list
    const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
    const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
    const [saving, setSaving] = useState(false);
    const [targetCategoryId, setTargetCategoryId] = useState('');

    const handleGenerate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!jobTitle.trim()) return toast.error('Job Title is required');
        
        setGenerating(true);
        try {
            const res = await api.post('/questions/generate', {
                jobTitle, keySkills, difficulty, type, count
            });
            const items = res.data || [];
            setGeneratedQuestions(items);
            // Default select all
            setSelectedIndexes(new Set(items.map((_: any, idx: number) => idx)));
            setStep('preview');
            toast.success(`Generated ${items.length} questions!`);
        } catch (err: any) {
            toast.error(err.message || 'AI Generation failed');
        } finally {
            setGenerating(false);
        }
    };

    const handleSaveGenerated = async () => {
        const selected = generatedQuestions.filter((_, idx) => selectedIndexes.has(idx));
        if (selected.length === 0) return toast.error('Select at least one question to save');

        setSaving(true);
        try {
            for (const q of selected) {
                // Derive correctOptionIds from options[].isCorrect for MCQ questions
                const config = { ...q.config };
                if ((q.type || type) === 'mcq' && config.options && !config.correctOptionIds) {
                    config.correctOptionIds = config.options
                        .filter((o: any) => o.isCorrect)
                        .map((o: any) => o.id);
                }
                // Ensure type & difficulty are correct
                await api.post('/questions', {
                    type: q.type || type,
                    prompt: q.prompt,
                    config,
                    difficulty: q.difficulty || difficulty,
                    categoryId: targetCategoryId || null,
                    tags: [jobTitle.toLowerCase(), 'ai-generated']
                });
            }
            toast.success(`Saved ${selected.length} questions successfully!`);
            onSaved();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save questions');
        } finally {
            setSaving(false);
        }
    };

    const toggleSelect = (idx: number) => {
        setSelectedIndexes(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };

    const updateQuestionPrompt = (idx: number, newPrompt: string) => {
        setGeneratedQuestions(prev => prev.map((q, i) => i === idx ? { ...q, prompt: newPrompt } : q));
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: step === 'input' ? 500 : 760, maxHeight: '90vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-6">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Sparkles size={20} color="var(--color-accent)" /> AI Question Generator
                    </h2>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                </div>

                {step === 'input' ? (
                    <form onSubmit={handleGenerate}>
                        <div className="form-group">
                            <label className="form-label">Target Job Role *</label>
                            <input className="form-input" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Senior React Developer, Sales Executive" required />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Core Skills / Topics (Optional)</label>
                            <input className="form-input" value={keySkills} onChange={e => setKeySkills(e.target.value)} placeholder="e.g. Hooks, Context API, Redux, Performance" />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Question Type</label>
                            <select className="form-select" value={type} onChange={e => setType(e.target.value as any)}>
                                <option value="mcq">🔤 Multiple Choice (MCQ)</option>
                                <option value="subjective">✍️ Subjective / Essay</option>
                                <option value="speaking">🗣️ English speaking video test</option>
                            </select>
                        </div>

                        <div className="flex gap-4">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">Difficulty</label>
                                <select className="form-select" value={difficulty} onChange={e => setDifficulty(e.target.value as any)}>
                                    <option value="easy">Easy</option>
                                    <option value="medium">Medium</option>
                                    <option value="hard">Hard</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">Count</label>
                                <input type="number" className="form-input" min={1} max={10} value={count} onChange={e => setCount(Math.min(Number(e.target.value), 10))} />
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary w-full mt-4" disabled={generating} style={{ background: 'var(--color-accent-gradient)', border: 'none' }}>
                            {generating ? (
                                <>Generating Questions...</>
                            ) : (
                                <>
                                    Generate Questions <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>
                ) : (
                    <div>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: 20 }}>
                            Review, edit, and select the generated questions you'd like to import into your bank.
                        </p>

                        <div className="form-group">
                            <label className="form-label">Select Destination Folder</label>
                            <select className="form-select" value={targetCategoryId} onChange={e => setTargetCategoryId(e.target.value)}>
                                <option value="">-- General / Unassigned --</option>
                                {categories.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 400, overflowY: 'auto', marginBottom: 24, padding: 2 }}>
                            {generatedQuestions.map((q, idx) => {
                                const isSelected = selectedIndexes.has(idx);
                                return (
                                    <div key={idx} style={{
                                        padding: 16, background: 'var(--color-bg-tertiary)', borderRadius: 12,
                                        border: isSelected ? '2px solid var(--color-accent)' : '2px solid transparent',
                                        transition: 'all 0.15s ease'
                                    }}>
                                        <div className="flex gap-3 mb-3">
                                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(idx)}
                                                style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 3, accentColor: 'var(--color-accent)' }} />
                                            <div style={{ flex: 1 }}>
                                                <textarea
                                                    className="form-textarea"
                                                    value={q.prompt}
                                                    onChange={e => updateQuestionPrompt(idx, e.target.value)}
                                                    rows={2}
                                                    style={{ background: 'var(--color-bg-card)', fontSize: '14px', border: '1px solid var(--color-border)', width: '100%' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Show MCQ options to preview/check */}
                                        {type === 'mcq' && q.config?.options && (
                                            <div style={{ paddingLeft: 30, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {q.config.options.map((opt: any, optIdx: number) => (
                                                    <div key={optIdx} className="flex items-center gap-2" style={{ fontSize: '13px' }}>
                                                        <div style={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            background: opt.isCorrect ? 'var(--color-success)' : 'transparent',
                                                            border: '1px solid var(--color-text-muted)'
                                                        }} />
                                                        <span style={{ color: opt.isCorrect ? 'var(--color-success)' : 'var(--color-text-secondary)', fontWeight: opt.isCorrect ? 600 : 400 }}>
                                                            {opt.text}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Show subjective rubric preview */}
                                        {(type === 'subjective' || type === 'speaking') && q.config?.rubric && (
                                            <div style={{ paddingLeft: 30, fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                                <strong>Grading rubrics:</strong> {q.config.rubric.map((c: any) => `${c.name} (${c.weight}%)`).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex gap-3">
                            <button className="btn btn-secondary flex-1" onClick={() => setStep('input')}>← Back</button>
                            <button className="btn btn-primary flex-1" onClick={handleSaveGenerated} disabled={saving}>
                                <Save size={16} /> {saving ? 'Saving...' : `Import Selected (${selectedIndexes.size})`}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
